import * as React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, FormikProps } from 'formik';
import moment from 'moment';
import styled from 'styled-components';
import { Select } from 'antd';

import { Modal } from 'atoms';
import { PopupNames } from 'core';
import { usePaginatedGroupsQuery } from 'core/graphql';
import {
  usePopupData,
  usePopupStateOperations,
  useSurveys,
  useGroupLessons,
  useSurveyAssignmentMutations,
} from 'core/hooks';
import { Colors } from 'services/ColorService';

import { PopupContainer } from '../PopupContainer';

/**
 * popupData shape — two entry modes (spec 0011):
 *  - group-first (group card → Surveys tab): `{ groupId, courseId }` — the
 *    group is fixed, the manager picks a SURVEY + a lesson.
 *  - survey-first (surveys list → «Прикрепить к группе»): `{ surveyId,
 *    surveyTitle, courseId, defaultLessonNumber }` — the survey is fixed, the
 *    manager searches a GROUP (then a lesson of it).
 */
export interface IAttachSurveyPopupData {
  groupId?: string;
  surveyId?: string;
  surveyTitle?: string;
  /** The course id — narrows survey options (group-first) or shown as a hint
   * (survey-first). */
  courseId?: string;
  /** Survey template's «после занятия N» hint, used to prefill the lesson. */
  defaultLessonNumber?: number;
}

interface IAttachFormValues {
  surveyId: string;
  lessonId: string;
}

const GROUP_SEARCH_DEBOUNCE_MS = 300;

/**
 * «Прикрепить опрос». The lesson select lists the effective group's schedule
 * grouped by module, labelled `№{flatIdx}. {name} — {DD.MM}` where `flatIdx`
 * is the 1-based position in the flat `(module.id, lesson.number)` order — the
 * same canonical order the backend uses for `lessonNumber`. A survey with
 * `defaultLessonNumber = N` preselects the N-th lesson of that flat list
 * (skipped when N is out of range). Server-side DUPLICATE_ASSIGNMENT /
 * COURSE_MISMATCH come back as a form-level error.
 */
export const AttachSurveyPopup = memo(() => {
  const { t } = useTranslation();
  const data = usePopupData(PopupNames.attachSurveyPopup);
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();

  const isSurveyFirst = Boolean(data?.surveyId && !data?.groupId);

  // survey-first: the group is picked here; group-first: it's fixed in data.
  const [pickedGroupId, setPickedGroupId] = useState('');
  const effectiveGroupId = data?.groupId ?? pickedGroupId;

  // Debounced server-side group search (survey-first only).
  const [groupSearch, setGroupSearch] = useState('');
  const [debouncedGroupSearch, setDebouncedGroupSearch] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedGroupSearch(groupSearch.trim()),
      GROUP_SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [groupSearch]);

  const { surveys, loading: surveysLoading } = useSurveys({
    courseId: data?.courseId,
  });
  const { data: groupsData, loading: groupsLoading } = usePaginatedGroupsQuery({
    // Список групп для выбора — только id и имя: закрытые ролями поля
    // (`price`, `currency` — `GROUP_STAFF_FIELDS_ROLES`) не нужны и не
    // запрашиваются, иначе методисту приходил отказ поля.
    variables: {
      query: debouncedGroupSearch || undefined,
      first: 20,
      withStaffFields: false,
    },
    skip: !isSurveyFirst,
    fetchPolicy: 'cache-and-network',
  });
  const { group, loading: lessonsLoading } = useGroupLessons(effectiveGroupId);
  const { attach, pending } = useSurveyAssignmentMutations();

  const groupOptions = useMemo(
    () =>
      (groupsData?.allGroups?.edges || [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map((node) => ({ value: node.id, label: node.name })),
    [groupsData],
  );

  const flatLessons = useMemo(() => {
    const lessons = (
      group?.lessons || []
    ).filter((lesson): lesson is NonNullable<typeof lesson> => Boolean(lesson));
    const sorted = [...lessons].sort((a, b) => {
      const aModule = Number(a.module.id);
      const bModule = Number(b.module.id);
      if (aModule !== bModule) return aModule - bModule;
      return (a.number ?? 0) - (b.number ?? 0);
    });
    return sorted.map((lesson, index) => ({ lesson, flatIdx: index + 1 }));
  }, [group]);

  const moduleGroups = useMemo(() => {
    const groups: Array<{
      moduleId: string;
      moduleName: string;
      items: typeof flatLessons;
    }> = [];
    flatLessons.forEach((item) => {
      const last = groups[groups.length - 1];
      if (last && last.moduleId === item.lesson.module.id) {
        last.items.push(item);
      } else {
        groups.push({
          moduleId: item.lesson.module.id,
          moduleName: item.lesson.module.name,
          items: [item],
        });
      }
    });
    return groups;
  }, [flatLessons]);

  const lessonLabel = useCallback(
    (item: typeof flatLessons[number]) =>
      `№${item.flatIdx}. ${item.lesson.name}${
        item.lesson.date ? ` — ${moment(item.lesson.date).format('DD.MM')}` : ''
      }`,
    [],
  );

  const prefillLesson = useCallback(
    (
      lessonNumber: number | null | undefined,
      setFieldValue: (field: string, value: string) => void,
    ) => {
      if (
        lessonNumber &&
        lessonNumber >= 1 &&
        lessonNumber <= flatLessons.length
      ) {
        setFieldValue('lessonId', flatLessons[lessonNumber - 1].lesson.id);
      }
    },
    [flatLessons],
  );

  // survey-first: once the picked group's lessons load, prefill the trigger
  // lesson from the survey's defaultLessonNumber (group-first does it on the
  // survey-select onChange instead).
  const formikRef = useRef<FormikProps<IAttachFormValues>>(null);
  useEffect(() => {
    if (!isSurveyFirst || !flatLessons.length) return;
    const form = formikRef.current;
    if (form && !form.values.lessonId) {
      prefillLesson(data?.defaultLessonNumber, form.setFieldValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSurveyFirst, flatLessons, data?.defaultLessonNumber]);

  const close = useCallback(() => hidePopup(PopupNames.attachSurveyPopup), [
    hidePopup,
  ]);

  if (!data) return null;

  return (
    <Modal>
      <PopupContainer
        width="520px"
        title={t('pages.surveys.attachPopup.title')}
        onClose={close}
      >
        <Formik<IAttachFormValues>
          innerRef={formikRef}
          initialValues={{ surveyId: '', lessonId: '' }}
          onSubmit={async (values, { setStatus }) => {
            setStatus(undefined);
            const surveyId = data.surveyId ?? values.surveyId;
            if (!surveyId || !effectiveGroupId || !values.lessonId) return;
            const result = await attach({
              surveyId,
              groupId: effectiveGroupId,
              triggerLessonId: values.lessonId,
            });
            if (result.success) {
              close();
            } else {
              setStatus(result.errorMessage);
            }
          }}
        >
          {({ values, status, handleSubmit, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <FormBody>
                {isSurveyFirst ? (
                  <>
                    <FixedField>
                      <FormLabelText>
                        {t('pages.surveys.attachPopup.forSurvey')}
                      </FormLabelText>
                      <FixedValue>
                        {data.surveyTitle}
                        {data.courseId
                          ? ''
                          : ` (${t('pages.surveys.list.universal')})`}
                      </FixedValue>
                    </FixedField>

                    <FormLabel>
                      {t('pages.surveys.attachPopup.group')}
                      <Select
                        showSearch
                        filterOption={false}
                        value={pickedGroupId || undefined}
                        placeholder={t(
                          'pages.surveys.attachPopup.groupPlaceholder',
                        )}
                        notFoundContent={
                          groupsLoading
                            ? t('pages.surveys.attachPopup.loading')
                            : t('pages.surveys.attachPopup.groupSearchHint')
                        }
                        loading={groupsLoading}
                        onSearch={setGroupSearch}
                        onChange={(value: string) => {
                          setPickedGroupId(value);
                          setFieldValue('lessonId', '');
                        }}
                        options={groupOptions}
                        style={{ width: '100%' }}
                      />
                    </FormLabel>
                  </>
                ) : (
                  <FormLabel>
                    {t('pages.surveys.attachPopup.survey')}
                    <FormSelect
                      value={values.surveyId}
                      onChange={(event) => {
                        const surveyId = event.target.value;
                        setFieldValue('surveyId', surveyId);
                        const survey = surveys.find(
                          (item) => item.id === surveyId,
                        );
                        prefillLesson(
                          survey?.defaultLessonNumber,
                          setFieldValue,
                        );
                      }}
                    >
                      <option value="">
                        {surveysLoading
                          ? t('pages.surveys.attachPopup.loading')
                          : t('pages.surveys.attachPopup.surveyPlaceholder')}
                      </option>
                      {surveys.map((survey) => (
                        <option key={survey.id} value={survey.id}>
                          {survey.title}
                          {survey.courseName
                            ? ` (${survey.courseName})`
                            : ` (${t('pages.surveys.list.universal')})`}
                        </option>
                      ))}
                    </FormSelect>
                  </FormLabel>
                )}

                <FormLabel>
                  {t('pages.surveys.attachPopup.lesson')}
                  <FormSelect
                    value={values.lessonId}
                    disabled={!effectiveGroupId}
                    onChange={(event) =>
                      setFieldValue('lessonId', event.target.value)
                    }
                  >
                    <option value="">
                      {!effectiveGroupId
                        ? t('pages.surveys.attachPopup.lessonPickGroupFirst')
                        : lessonsLoading
                        ? t('pages.surveys.attachPopup.loading')
                        : t('pages.surveys.attachPopup.lessonPlaceholder')}
                    </option>
                    {moduleGroups.map((moduleGroup) => (
                      <optgroup
                        key={moduleGroup.moduleId}
                        label={moduleGroup.moduleName}
                      >
                        {moduleGroup.items.map((item) => (
                          <option key={item.lesson.id} value={item.lesson.id}>
                            {lessonLabel(item)}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </FormSelect>
                </FormLabel>

                {status ? <FormError>{status}</FormError> : null}

                <FormFooter>
                  <ActionButton
                    type="button"
                    onClick={close}
                    disabled={pending}
                  >
                    {t('pages.surveys.attachPopup.cancel')}
                  </ActionButton>
                  <ActionButton
                    type="submit"
                    primary
                    disabled={
                      pending ||
                      !effectiveGroupId ||
                      !values.lessonId ||
                      (!isSurveyFirst && !values.surveyId)
                    }
                  >
                    {pending
                      ? t('pages.surveys.attachPopup.submitting')
                      : t('pages.surveys.attachPopup.submit')}
                  </ActionButton>
                </FormFooter>
              </FormBody>
            </form>
          )}
        </Formik>
      </PopupContainer>
    </Modal>
  );
});

const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 16px;
`;

const FormLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #667085;
`;

const FixedField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FormLabelText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #667085;
`;

const FixedValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #101828;
`;

const FormSelect = styled.select`
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 14px;
  background: ${Colors.White};
  outline: none;
  font-weight: 500;
  color: #101828;

  &:focus {
    border-color: #2e90fa;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FormError = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${Colors.Red};
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

const ActionButton = styled.button<{ primary?: boolean }>`
  border: 1px solid ${({ primary }) => (primary ? '#2e90fa' : '#e4e7ec')};
  background: ${({ primary }) => (primary ? '#2e90fa' : Colors.White)};
  color: ${({ primary }) => (primary ? Colors.White : '#344054')};
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
